import { BreakoutRoomsState } from 'pages/useBreakouts'
import { BookingContext } from 'contexts/booking'
import { ConferenceContext } from 'contexts/conference'
import { ConferenceParticipant, RtcSession } from 'contexts/conference/types'
import { useContext, useEffect, useRef } from 'react'
import { useFeatureCheck } from 'hooks/useFeatureCheck'
import { useFeature } from '@growthbook/growthbook-react'
import { BreakoutRoom } from 'api/classroom/ClassroomState'
import { WseUserId } from 'api/types'
import { getRoomIdForUser } from 'pages/breakoutHelpers'
import { formatRoomAssignments, logLocalAudio } from 'services/breakoutFaroLogger'
import { rollbarService, RollbarExceptionNames } from 'services/rollbar'
import { isGocAssistant } from 'utils/is-goc-assistant'

const order = (others: ConferenceParticipant[]): ConferenceParticipant[] => {
    const ordered = [...others]
    const teacher = ordered.find(p => p.role === 'teacher')
    if (teacher) {
        ordered.unshift(...ordered.splice(ordered.indexOf(teacher), 1)) // put teacher first
    }

    return ordered
}

const getParticipantRoom = (breakouts: BreakoutRoomsState, wseUserId: string): number => {
    const room = breakouts.rooms.find(r => r.users.includes(wseUserId))
    return room?.id ?? -1
}

const logLocalAudioOperationError = (
    operation: 'unmute' | 'exclude',
    participant: ConferenceParticipant,
    error: unknown
): void => {
    rollbarService.rollbar.error(
        operation === 'unmute'
            ? RollbarExceptionNames.UnmuteLocallyError
            : RollbarExceptionNames.ExcludeLocallyError,
        {
            targetUserId: participant.wseUserId,
            clientUserId: participant.clientUserId,
            source: 'layout',
            error: error instanceof Error ? error.message : String(error)
        }
    )
}

const AUDIO_RETRY_ATTEMPTS = 3
const AUDIO_RETRY_BASE_DELAY_MS = 300
export const LOCAL_MUTE_CHECK_INTERVAL_MS = 3000

const hasNumericClientUserId = (clientUserId: string): boolean => /^\d+$/.test(clientUserId)

const applyCrossRoomLocalMute = async (
    rtcSession: RtcSession,
    participant: ConferenceParticipant
): Promise<void> => {
    await rtcSession.muteLocally(participant)
    await rtcSession.adjustUserAudioVolumeLocally?.(+participant.clientUserId, 0)
}

/**
 * Periodic safety net: unconditionally re-applies local mute + volume 0 for every
 * cross-room participant on each tick (every LOCAL_MUTE_CHECK_INTERVAL_MS).
 */
const resolveParticipantRoom = (
    rooms: BreakoutRoom[],
    participant: ConferenceParticipant,
    teacherRoom: number | null
): number | null =>
    participant.role === 'teacher'
        ? teacherRoom
        : getRoomIdForUser(rooms, participant.wseUserId)

export const checkCrossRoomLocalMuteState = async (
    rtcSession: RtcSession | undefined,
    rooms: BreakoutRoom[],
    liveParticipants: ConferenceParticipant[],
    viewerRoom: number | null,
    viewerRole: string,
    viewerUserId: WseUserId,
    teacherRoom: number | null = null
): Promise<void> => {
    if (!rtcSession) {
        return
    }

    if (viewerRole === 'student' && viewerRoom === null) {
        return
    }

    for (const participant of liveParticipants) {
        if (participant.wseUserId === viewerUserId) {
            continue
        }

        if (!hasNumericClientUserId(participant.clientUserId)) {
            continue
        }

        const targetRoom = resolveParticipantRoom(rooms, participant, teacherRoom)
        if (targetRoom === null || targetRoom === viewerRoom) {
            continue
        }

        try {
            await applyCrossRoomLocalMute(rtcSession, participant)
        } catch {
            // Continue checking remaining participants
        }
    }
}

export const executeWithRetry = async (operation: () => Promise<void>, maxRetries: number, baseDelayMs: number): Promise<void> => {
    let lastError: unknown

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await operation()
        } catch (error) {
            lastError = error
            if (attempt < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, baseDelayMs * (attempt + 1)))
            }
        }
    }

    throw lastError
}

type AudioOperation = 'unmute' | 'exclude'

type AppliedAudioState = {
    operation: AudioOperation
    audioStatus: ConferenceParticipant['audioStatus']
    audioMuted: ConferenceParticipant['audioMuted']
}

type AudioTarget = {
    operation: AudioOperation
    participant: ConferenceParticipant
    audioStatus: ConferenceParticipant['audioStatus']
    audioMuted: ConferenceParticipant['audioMuted']
    targetRoom: number
    viewerRoom: number | null
    viewerRole: string
    roomAssignments?: string
    zoomLiveParticipantIds?: string
    zoomLiveParticipantClientIds?: string
    usedZoomLiveParticipants?: boolean
}

/**
 * Applies one audio operation (unmute/exclude + volume) with retry and per-participant serialization.
 * Operations for the same participant are chained onto any in-flight one, so rapid room
 * changes reach the Zoom SDK in order instead of racing - the last queued operation wins.
 */
export const runAudioOperation = (
    rtcSession: RtcSession,
    target: AudioTarget,
    pendingOperations: Map<string, Promise<void>>
): void => {
    const participantId = target.participant.wseUserId

    const operation = async (): Promise<void> => {
        const clientUserId = target.participant.clientUserId
        const numericClientUserId = hasNumericClientUserId(clientUserId) ? Number(clientUserId) : null

        if (target.operation === 'unmute') {
            await rtcSession.unmuteLocally(target.participant)
            if (numericClientUserId !== null) {
                await rtcSession.adjustUserAudioVolumeLocally?.(numericClientUserId, 100)
            }
        } else {
            await rtcSession.excludeLocally(target.participant)
            if (numericClientUserId !== null) {
                await rtcSession.adjustUserAudioVolumeLocally?.(numericClientUserId, 0)
            }
        }
    }

    const previous = pendingOperations.get(participantId) ?? Promise.resolve()
    const chained = previous
        .catch(() => undefined) // a failed predecessor is already logged - it must not block the queue
        .then(async () => await executeWithRetry(operation, AUDIO_RETRY_ATTEMPTS, AUDIO_RETRY_BASE_DELAY_MS))

    pendingOperations.set(participantId, chained)

    void (async () => {
        try {
            await chained
        } catch (error) {
            logLocalAudioOperationError(target.operation, target.participant, error)
        } finally {
            if (pendingOperations.get(participantId) === chained) {
                pendingOperations.delete(participantId)
            }
        }
    })()
}

const getOthers = (participants: ConferenceParticipant[], currentUserId: string): ConferenceParticipant[] => {
    return participants.filter(participant => participant.wseUserId !== currentUserId && !isGocAssistant(participant))
}

const getList = (
    others: ConferenceParticipant[],
    breakouts: BreakoutRoomsState | undefined
): ConferenceParticipant[] => {
    if (!breakouts?.isActive) {
        return order(others)
    }

    const currentRoom = breakouts.getCurrentRoom()
    const roomUsersIds = currentRoom !== null
        ? breakouts.rooms[currentRoom]?.users
        : undefined

    if (!roomUsersIds) {
        return order(others)
    }

    return order(others.filter(participant => {
        const isTeacherJoined = participant.role === 'teacher' && breakouts.teacherRoom === currentRoom
        const isAssigned = roomUsersIds.includes(participant.wseUserId)
        return isAssigned || isTeacherJoined
    }))
}

const getAudioTargets = (
    others: ConferenceParticipant[],
    participantsForLayout: ConferenceParticipant[],
    breakouts: BreakoutRoomsState | undefined,
    isSubsessionsEnabled: boolean,
    viewerRole: string,
    usedZoomLiveParticipants: boolean
): AudioTarget[] => {
    if (isSubsessionsEnabled) {
        return []
    }

    const localAudioContext = {
        roomAssignments: breakouts?.isActive ? formatRoomAssignments(breakouts.rooms) : undefined,
        zoomLiveParticipantIds: participantsForLayout.map(p => p.wseUserId).join(','),
        zoomLiveParticipantClientIds: participantsForLayout.map(p => p.clientUserId).join(','),
        usedZoomLiveParticipants
    }

    if (!breakouts?.isActive) {
        return others.map(participant => ({
            operation: 'unmute',
            participant,
            audioStatus: participant.audioStatus,
            audioMuted: participant.audioMuted,
            targetRoom: -1,
            viewerRoom: null,
            viewerRole,
            ...localAudioContext
        }))
    }

    // Teachers are never assigned to a room - they hear the room they have joined (teacherRoom)
    const currentRoom = viewerRole === 'teacher'
        ? breakouts.teacherRoom
        : breakouts.getCurrentRoom()
    const roomUsersIds = currentRoom !== null
        ? breakouts.rooms[currentRoom]?.users
        : undefined

    return others.map(participant => {
        const isAssigned = roomUsersIds?.includes(participant.wseUserId) ?? false
        const isTeacherJoined = participant.role === 'teacher' && breakouts.teacherRoom === currentRoom

        return {
            operation: isAssigned || isTeacherJoined ? 'unmute' : 'exclude',
            participant,
            audioStatus: participant.audioStatus,
            audioMuted: participant.audioMuted,
            targetRoom: getParticipantRoom(breakouts, participant.wseUserId),
            viewerRoom: currentRoom,
            viewerRole,
            ...localAudioContext
        }
    })
}

/**
 * Single source of truth for local audio isolation during breakouts.
 * Unmutes same-room participants and excludes (mutes + detaches video) cross-room participants.
 * When breakouts are inactive it unmutes everyone, restoring default audio.
 *
 * Mounting contract: exactly one layout mounts this hook at a time -
 * ZoomLayout (normal sessions + students) or BorZoomLayout (teacher during breakouts).
 */
export const useBreakoutAudioIsolation = (breakouts?: BreakoutRoomsState): void => {
    const conference = useContext(ConferenceContext)
    const booking = useContext(BookingContext)
    const isSubsessionsEnabled = useFeature('goc-sub-session').value as boolean
    const isReady = conference.status === 'session-joined' && booking.status === 'validated'
    const rtcSession = isReady ? conference.rtcSession : undefined
    const viewerUserId = isReady ? booking.user.id : null
    const viewerRole = isReady ? booking.user.role : 'unknown'
    const participantsForLayout = isReady
        ? rtcSession?.getLiveParticipants?.() ?? conference.participants
        : []
    const others = isReady ? getOthers(participantsForLayout, booking.user.id) : []
    const audioTargetsRef = useRef<AudioTarget[]>([])
    const appliedAudioOperationsRef = useRef<Map<string, AppliedAudioState>>(new Map())
    const pendingAudioOperationsRef = useRef<Map<string, Promise<void>>>(new Map())
    const audioTargets = getAudioTargets(
        others,
        participantsForLayout,
        breakouts,
        isSubsessionsEnabled,
        viewerRole,
        !!rtcSession?.getLiveParticipants
    )
    audioTargetsRef.current = audioTargets
    const audioTargetSignature = audioTargets
        .map(target => `${target.participant.wseUserId}:${target.operation}:${target.audioStatus}:${String(target.audioMuted)}`)
        .join('|')

    useEffect(() => {
        if (!isReady || !rtcSession) {
            appliedAudioOperationsRef.current.clear()
            return
        }

        const activeParticipantIds = new Set(audioTargetsRef.current.map(target => target.participant.wseUserId))
        appliedAudioOperationsRef.current.forEach((_, participantId) => {
            if (!activeParticipantIds.has(participantId)) {
                appliedAudioOperationsRef.current.delete(participantId)
            }
        })

        audioTargetsRef.current.forEach(target => {
            const participantId = target.participant.wseUserId
            const previousState = appliedAudioOperationsRef.current.get(participantId)
            const operationChanged = previousState?.operation !== target.operation
            // Any audioStatus (off->on / on->off) or audioMuted flip means the SDK audio track
            // transitioned, so the local mute policy must be re-applied for that participant
            const audioStateChanged = previousState?.audioStatus !== target.audioStatus ||
                previousState?.audioMuted !== target.audioMuted

            if (previousState && !operationChanged && !audioStateChanged) {
                appliedAudioOperationsRef.current.set(participantId, {
                    operation: target.operation,
                    audioStatus: target.audioStatus,
                    audioMuted: target.audioMuted
                })
                return
            }

            if (target.roomAssignments !== undefined) {
                logLocalAudio({
                    action: target.operation,
                    targetUserId: participantId,
                    clientUserId: target.participant.clientUserId,
                    targetRoom: target.targetRoom,
                    viewerRoom: target.viewerRoom,
                    viewerRole: target.viewerRole,
                    source: 'layout',
                    roomAssignments: target.roomAssignments,
                    zoomLiveParticipantIds: target.zoomLiveParticipantIds,
                    zoomLiveParticipantClientIds: target.zoomLiveParticipantClientIds,
                    usedZoomLiveParticipants: target.usedZoomLiveParticipants
                })
            }

            runAudioOperation(rtcSession, target, pendingAudioOperationsRef.current)

            appliedAudioOperationsRef.current.set(participantId, {
                operation: target.operation,
                audioStatus: target.audioStatus,
                audioMuted: target.audioMuted
            })
        })
    }, [rtcSession, isReady, audioTargetSignature])

    useEffect(() => {
        if (!isReady || !rtcSession || !breakouts?.isActive || isSubsessionsEnabled || !viewerUserId) {
            return
        }

        const getViewerRoom = (): number | null =>
            viewerRole === 'teacher' ? breakouts.teacherRoom : breakouts.getCurrentRoom()

        const runLocalMuteCheck = (): void => {
            void checkCrossRoomLocalMuteState(
                rtcSession,
                breakouts.rooms,
                rtcSession.getLiveParticipants?.() ?? participantsForLayout,
                getViewerRoom(),
                viewerRole,
                viewerUserId,
                breakouts.teacherRoom
            ).catch(error => {
                console.error('Periodic local mute check failed:', error)
            })
        }

        runLocalMuteCheck()
        const intervalId = window.setInterval(runLocalMuteCheck, LOCAL_MUTE_CHECK_INTERVAL_MS)

        return () => clearInterval(intervalId)
    }, [
        rtcSession,
        isReady,
        breakouts?.isActive,
        breakouts?.rooms,
        breakouts?.teacherRoom,
        isSubsessionsEnabled,
        viewerUserId,
        viewerRole,
        participantsForLayout
    ])
}

export const useFilterParticipants = (breakouts?: BreakoutRoomsState): ConferenceParticipant[] => {
    const conference = useContext(ConferenceContext)
    const booking = useContext(BookingContext)
    const { isFullFeaturedMode } = useFeatureCheck()
    const isReady = conference.status === 'session-joined' && booking.status === 'validated'
    const participantsForLayout = isReady
        ? conference.rtcSession?.getLiveParticipants?.() ?? conference.participants
        : []
    const others = isReady ? getOthers(participantsForLayout, booking.user.id) : []
    const list = getList(others, breakouts)

    useBreakoutAudioIsolation(breakouts)

    if (isFullFeaturedMode) {
        return list

    } else {
        if (conference.status === 'session-joined') {
            const lastSpeaker = list.find(participant => participant.wseUserId === conference.lastSpeakerId)
            if (lastSpeaker) {
                for (const participant of list) {
                    if (participant.wseUserId !== conference.lastSpeakerId) {
                        void conference.rtcSession.stopRenderVideo(participant)
                    }
                }

                return [lastSpeaker]

            } else {
                return list.slice(0, 1)
            }

        } else {
            return []
        }
    }

}
